import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminProductDetail, ProductLine } from '@popcart/core/v1';
import { ApiRequestError, api, errorMessage } from '../api';
import { ConfirmDialog, LineChip, LineDot, Loading, Segmented, useTitle } from '../ui/bits';
import { Stat } from '../ui/viz';
import { Meter, StDot, dotTone } from './kit';
import { peso } from '../lib/format.js';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/toast';
import { pickerOptions } from './lib/categories.js';
import {
  buildInput,
  buildPatch,
  changeFreshPricing,
  changeLine,
  draftFromDetail,
  emptyDraft,
  firstError,
  isDirty,
  mapServerError,
  stockUnitOf,
  validateDraft,
  type ProductDraft,
} from './lib/draft.js';
import { LINE_LABEL, LOW_GRAMS, LOW_PIECES, STATUS_LABEL, STATUS_TONE, deleteCopy, deleteOutcome, deleteProblem, qtyLabel, stockLabel, stockState } from './lib/list.js';
import { useCategories, useInvalidateProduct, useRestore, useSetVisible } from './hooks';
import { catalogKeys } from './keys';
import { photoForm, prepareProductPhoto } from './photo';
import { StockAdjust } from './StockAdjust';
import { Field, FieldError, PesoInput, fid, invalid, type Errors, type SetField, type Update } from './editor/fields';
import { FreshSection } from './editor/FreshSection';
import { GrillSection } from './editor/GrillSection';
import { MartSection } from './editor/MartSection';
import { VariantsPanel } from './editor/VariantsPanel';
import { canAddVariant, variantsOf, type MartAdminDetail } from './lib/mart.js';
import { PhotoField } from './editor/PhotoField';
import { HistoryPanel } from './editor/HistoryPanel';
import { SceneError } from './art';
import './catalog.css';
import { PhotoBadge, ReviewBadge, ReviewNote } from './ReviewBadge';
import { needsPhoto, visibilityBlock } from './lib/photo-rule.js';
import { Select } from '../ui/controls';

export interface EditorProps {
  shopId: string;
  /** null = create */
  productId: string | null;
  /** The shop's product lines (the line picker offers only these). */
  lines: ProductLine[];
  shopName?: string;
  backTo: string;
  editPathFor(id: string): string;
  initialLine?: ProductLine | null;
}

/** The product editor shared by Partner Hub and Ops; it always works against `shopId`. */
export function ProductEditor(props: EditorProps) {
  const { shopId, productId } = props;
  const detail = useQuery({
    queryKey: catalogKeys.product(shopId, productId ?? ''),
    queryFn: () => api.adminProduct(shopId, productId ?? ''),
    enabled: !!productId && !!shopId,
  });
  if (!productId) return <EditorForm key="new" {...props} detail={null} />;
  if (detail.isError)
    return (
      <>
        <BackLink to={props.backTo} />
        <section className="card">
          <SceneError error={detail.error} onRetry={() => void detail.refetch()} />
        </section>
      </>
    );
  if (!detail.data) return <Loading label="Loading the product" rows={3} />;
  return <EditorForm key={productId} {...props} detail={detail.data} />;
}

function BackLink({ to }: { to: string }) {
  return (
    <Link className="dv-ibtn press cat-back" to={to} aria-label="Back to products" title="Back to products">
      <Icon name="chevronLeft" />
    </Link>
  );
}

function EditorForm({ shopId, lines, shopName, backTo, editPathFor, initialLine, detail }: EditorProps & { detail: AdminProductDetail | null }) {
  const isCreate = !detail;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  // Right after Create without a photo, the editor opens on the photo step (0013).
  const photoStep = !!(location.state as { photoStep?: boolean } | null)?.photoStep;
  const invalidate = useInvalidateProduct();
  const startLine: ProductLine = initialLine && lines.includes(initialLine) ? initialLine : (lines[0] ?? 'mart');

  const [original, setOriginal] = useState<ProductDraft | null>(() => (detail ? draftFromDetail(detail) : null));
  const [draft, setDraft] = useState<ProductDraft>(() => (detail ? draftFromDetail(detail) : emptyDraft(startLine)));
  const [errors, setErrors] = useState<Errors>({});
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  useTitle(isCreate ? 'New product' : (detail?.name ?? 'Product'));

  // A refetch (another tab, a stock change) refreshes the form unless there are unsaved edits.
  useEffect(() => {
    if (!detail) return;
    if (original && isDirty(draft, original)) return;
    const next = draftFromDetail(detail);
    setOriginal(next);
    setDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.updatedAt]);

  const categories = useCategories(draft.line);
  const catOptions = useMemo(
    () => pickerOptions(categories.data ?? [], draft.line, detail?.categoryId && detail.line === draft.line ? { id: detail.categoryId, name: detail.categoryName ?? 'Current category' } : null),
    [categories.data, draft.line, detail?.categoryId, detail?.categoryName, detail?.line],
  );

  const clear = (k: string) => setErrors((e) => (e[k] || e.form ? (({ [k]: _a, form: _b, ...rest }) => rest)(e) : e));
  const set: SetField = (k, v) => {
    setDraft((d) => ({ ...d, [k]: v }));
    clear(k as string);
  };
  const update: Update = (fn, k) => {
    setDraft(fn);
    if (k) clear(k);
  };

  const dirty = original ? isDirty(draft, original) : true;
  const archived = detail?.status === 'archived';

  const showErrors = (e: Errors) => {
    setErrors(e);
    const first = firstError(e);
    if (first && first !== 'form') window.setTimeout(() => document.getElementById(fid(first))?.focus(), 0);
  };

  const save = useMutation({
    mutationFn: async () => {
      if (isCreate) {
        const created = await api.createProduct(shopId, buildInput(draft, { isCreate: true }));
        let photoError: string | null = null;
        if (pendingPhoto) {
          try {
            const blob = await prepareProductPhoto(pendingPhoto);
            await api.uploadProductImage(shopId, created.id, photoForm(blob));
          } catch (err) {
            photoError = err instanceof Error && !(err as { status?: number }).status ? err.message : errorMessage(err, 'The photo didn’t upload.');
          }
        }
        return { product: created, photoError };
      }
      const patch = buildPatch(draft, original ?? draft);
      const product = await api.updateProduct(shopId, detail.id, patch);
      return { product, photoError: null };
    },
    onSuccess: ({ product, photoError }) => {
      invalidate(shopId);
      if (isCreate) {
        const uploaded = !!pendingPhoto && !photoError;
        toast(
          photoError
            ? `${product.name} is saved, but the photo didn’t upload: ${photoError} Add it again here.`
            : uploaded
              ? `${product.name} is saved.`
              : `${product.name} is saved. Add its photo next${product.publishOnPhoto ? ' to put it on sale' : ''}.`,
          photoError ? 'error' : 'info',
        );
        navigate(editPathFor(product.id), { replace: true, state: uploaded ? null : { photoStep: true } });
        return;
      }
      qc.setQueryData(catalogKeys.product(shopId, product.id), product);
      const next = draftFromDetail(product);
      setOriginal(next);
      setDraft(next);
      toast('Changes saved.');
    },
    onError: (err) => {
      const m = mapServerError(err as { status?: number; field?: string; message?: string });
      showErrors({ [m?.field ?? 'form']: m?.message ?? errorMessage(err, 'Couldn’t save. Try again.') });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (archived) return;
    const v = validateDraft(draft, {
      lines,
      isCreate,
      categories: categories.data ?? null,
      originalCategoryId: detail?.categoryId ?? null,
      selfId: detail?.id ?? null,
    });
    if (Object.keys(v).length) {
      showErrors(v);
      return;
    }
    if (!isCreate && !dirty) {
      toast('Nothing to save yet.');
      return;
    }
    setErrors({});
    save.mutate();
  };

  // Photo: upload straight away on an existing product; hold it until create otherwise.
  const photo = useMutation({
    mutationFn: async (file: File) => {
      if (!detail) throw new Error('no product');
      const blob = await prepareProductPhoto(file);
      return api.uploadProductImage(shopId, detail.id, photoForm(blob));
    },
    onSuccess: (p) => {
      qc.setQueryData(catalogKeys.product(shopId, p.id), p);
      invalidate(shopId);
      clear('photo');
      toast('Photo updated.');
    },
    onError: (err) => {
      const msg = err instanceof Error && !(err as { status?: number }).status ? err.message : errorMessage(err, 'The photo didn’t upload. Try again.');
      setErrors((e) => ({ ...e, photo: msg }));
    },
  });

  // The photo step: bring the (required) photo into view and focus its picker.
  useEffect(() => {
    if (!photoStep || !detail || !needsPhoto(detail)) return;
    const t = window.setTimeout(() => {
      document.getElementById('sec-photo')?.scrollIntoView({ block: 'center' });
      document.getElementById(fid('photo'))?.focus();
    }, 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoStep, detail?.id]);

  const visible = useSetVisible();
  const visibleError = visible.error instanceof ApiRequestError && visible.error.field === 'image' ? errorMessage(visible.error) : null;
  const blocked = detail ? visibilityBlock(detail) : null;
  const restore = useRestore();
  const del = useMutation({
    mutationFn: () => api.deleteProduct(shopId, detail?.id ?? ''),
    onSuccess: (res) => {
      toast(deleteOutcome(res.result, detail?.name ?? 'The product'));
      setConfirmDelete(false);
      qc.removeQueries({ queryKey: catalogKeys.product(shopId, detail?.id ?? '') });
      invalidate(shopId);
      navigate(backTo);
    },
    onError: (err) => {
      const problem = deleteProblem(err as { status?: number; message?: string });
      if (problem) setDeleteError(problem);
      else {
        setDeleteError(null);
        toast(errorMessage(err, 'Couldn’t delete it. Try again.'), 'error');
      }
    },
  });
  const copy = detail ? deleteCopy(detail) : null;

  const unit = stockUnitOf(draft);
  const martDetail = detail as MartAdminDetail | null;
  const lineLocked = !isCreate || lines.length < 2;

  return (
    <>
      <header className="dv-head cat-head">
        <BackLink to={backTo} />
        <div className="grow stack s4" style={{ minWidth: 0 }}>
          <h1>{isCreate ? 'New product' : detail.name}</h1>
          <span className="row wrap cat-headmeta">
            <span className="mk-st">
              <LineDot line={draft.line} />
              {LINE_LABEL[draft.line]}
            </span>
            {detail ? <StDot tone={dotTone(STATUS_TONE[detail.status])}>{STATUS_LABEL[detail.status]}</StDot> : null}
            {detail ? <ReviewBadge product={detail} /> : null}
            {detail ? <PhotoBadge product={detail} /> : null}
            {shopName ? <span className="mk-sub">{shopName}</span> : null}
          </span>
        </div>
      </header>

      {detail ? <EditorStats detail={detail} /> : null}

      {detail ? <ReviewNote product={detail} /> : null}

      {archived ? (
        <div className="note row wrap cat-archived" role="status">
          <Icon name="alert" />
          <span className="grow">This product is archived: customers can’t see it. Restore it to edit or sell it again. It comes back hidden.</span>
          <button type="button" className="btn" disabled={restore.isPending} onClick={() => detail && restore.mutate(detail)}>
            {restore.isPending ? 'Restoring…' : 'Restore'}
          </button>
        </div>
      ) : null}

      <div className="cat-editor">
        <form className="cat-main stack s24" onSubmit={onSubmit} noValidate aria-label={isCreate ? 'New product' : `Edit ${detail.name}`}>
          <fieldset className="cat-plain" disabled={archived}>
            <div className="stack s24">
              <Basics d={draft} errors={errors} set={set} update={update} lines={lines} lineLocked={lineLocked} catOptions={catOptions} catLoading={categories.isLoading} />
              {draft.line === 'fresh' ? <FreshSection d={draft} errors={errors} set={set} update={update} locked={!isCreate && (detail?.onHand ?? 0) !== (detail?.available ?? 0)} /> : null}
              {draft.line === 'grill' ? <GrillSection d={draft} errors={errors} set={set} update={update} shopId={shopId} selfId={detail?.id ?? null} /> : null}
              {draft.line === 'mart' ? (
                <MartSection d={draft} errors={errors} set={set} update={update} shopId={shopId} selfId={detail?.id ?? null} hasVariants={variantsOf(martDetail).length > 0} editPathFor={editPathFor} />
              ) : null}
              {isCreate ? <OpeningStock d={draft} errors={errors} set={set} unit={unit} /> : null}
            </div>
          </fieldset>

          <div className="cat-bar">
            {errors.form ? (
              <span className="pill st-error err-pill cat-wrap" role="alert">
                {errors.form}
              </span>
            ) : Object.keys(errors).length ? (
              <span className="pill st-error err-pill" role="alert">
                Fix the marked fields to save.
              </span>
            ) : (
              <span className="t-caption muted">{isCreate ? 'It goes on sale once it has a photo.' : dirty ? 'Unsaved changes.' : 'All changes saved.'}</span>
            )}
            <span className="grow" />
            <Link className="btn" to={backTo}>
              {isCreate || dirty ? 'Cancel' : 'Done'}
            </Link>
            <button type="submit" className="btn btn-primary" disabled={save.isPending || archived} aria-busy={save.isPending || undefined}>
              {save.isPending ? 'Saving…' : isCreate ? 'Add product' : 'Save changes'}
            </button>
          </div>
        </form>

        <aside className="cat-side stack s24" aria-label="Photo, visibility and stock">
          <PhotoField
            name={draft.name}
            line={draft.line}
            category={catOptions.find((c) => c.id === draft.categoryId)?.name ?? detail?.categoryName ?? null}
            imageUrl={detail?.imageUrl ?? null}
            pending={isCreate ? pendingPhoto : photo.isPending ? (photo.variables ?? null) : null}
            busy={photo.isPending}
            error={errors.photo}
            onPick={(f) => {
              clear('photo');
              if (isCreate) setPendingPhoto(f);
              else photo.mutate(f);
            }}
            onClear={isCreate ? () => setPendingPhoto(null) : undefined}
            missing={isCreate || needsPhoto(detail)}
            publishOnPhoto={isCreate ? draft.visible : !!detail?.publishOnPhoto}
            step={photoStep}
          />

          <section className="card panel" aria-labelledby="sec-vis">
            <h2 id="sec-vis">On sale</h2>
            {isCreate ? (
              <button type="button" role="switch" className="switch" aria-checked={draft.visible} onClick={() => set('visible', !draft.visible)}>
                <span className="track" aria-hidden="true" />
                <span className="stack s4">
                  <span className="t-label">{draft.visible ? (pendingPhoto ? 'Show to customers when saved' : 'Put it on sale when its photo is added') : 'Keep hidden for now'}</span>
                  <span className="t-caption muted">Every product needs a real photo to go on sale. Hidden products stay in your list but can’t be seen or ordered.</span>
                </span>
              </button>
            ) : archived ? (
              <p className="t-caption muted">Archived. Restore it first.</p>
            ) : (
              <>
                <button
                  type="button"
                  role="switch"
                  className="switch"
                  aria-checked={detail.status === 'active'}
                  aria-describedby={blocked ? 'vis-why' : undefined}
                  disabled={visible.isPending || !!blocked}
                  onClick={() => visible.mutate({ product: detail, visible: detail.status !== 'active' })}
                >
                  <span className="track" aria-hidden="true" />
                  <span className="stack s4">
                    <span className="t-label">{detail.status === 'active' ? 'Customers can see and order it' : 'Hidden from customers'}</span>
                    <span id="vis-why" className="t-caption muted">
                      {blocked ?? (needsPhoto(detail) ? 'On sale from before photos were required. Add one; if you hide it, it needs the photo to come back.' : 'Takes effect right away.')}
                    </span>
                  </span>
                </button>
                {blocked && detail.publishOnPhoto ? (
                  <button type="button" className="btn btn-quiet" disabled={visible.isPending} onClick={() => visible.mutate({ product: detail, visible: false })}>
                    Keep it hidden after the photo
                  </button>
                ) : null}
                {visibleError ? (
                  <span className="pill st-error err-pill cat-wrap" role="alert">
                    {visibleError}
                  </span>
                ) : null}
              </>
            )}
          </section>

          {detail ? (
            <section className="card panel" aria-labelledby="sec-stock">
              <h2 id="sec-stock">Stock</h2>
              {detail.isCombo ? (
                <p className="t-caption muted">{detail.line === 'mart' ? 'A pack’s' : 'A combo’s'} stock comes from its items. Adjust those products instead.</p>
              ) : (
                <>
                  <span className="cat-stocknow">{stockLabel(detail)}</span>
                  {archived ? null : <StockAdjust product={detail} />}
                </>
              )}
            </section>
          ) : null}

          {martDetail && (canAddVariant(martDetail) || variantsOf(martDetail).length) ? <VariantsPanel shopId={shopId} product={martDetail} editPathFor={editPathFor} /> : null}

          {detail ? <HistoryPanel shopId={shopId} productId={detail.id} updatedAt={detail.updatedAt} /> : null}

          {detail && !archived && copy ? (
            <section className="card panel" aria-labelledby="sec-del">
              <h2 id="sec-del">{copy.kind === 'archive' ? 'Archive' : 'Delete'}</h2>
              <p className="t-caption muted">
                {copy.kind === 'archive' ? 'It has past orders, so it will be archived (kept for receipts) rather than deleted.' : 'It has never been ordered, so it can be deleted for good.'}
              </p>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setDeleteError(null);
                  setConfirmDelete(true);
                }}
              >
                {copy.kind === 'archive' ? 'Archive product…' : 'Delete product…'}
              </button>
            </section>
          ) : null}
        </aside>
      </div>

      {copy ? (
        <ConfirmDialog
          open={confirmDelete}
          title={copy.title}
          body={
            <div className="stack s12">
              <p>{copy.body}</p>
              {deleteError ? (
                <span className="pill st-error err-pill cat-wrap" role="alert">
                  {deleteError}
                </span>
              ) : null}
            </div>
          }
          confirmLabel={copy.confirm}
          busy={del.isPending}
          onConfirm={() => del.mutate()}
          onClose={() => setConfirmDelete(false)}
        />
      ) : null}
    </>
  );
}

/** The product's numbers on top: price, what's on the shelf, its category. */
function EditorStats({ detail }: { detail: AdminProductDetail }) {
  const weight = detail.pricing === 'weight';
  const amount = peso((weight ? detail.pricePerKgCentavos : detail.priceCentavos) ?? 0);
  const unit = weight ? 'per kg' : detail.unitLabel ? `per ${detail.unitLabel.replace(/^\s*\/\s*/, '')}` : 'each';
  const state = stockState(detail);
  const full = (detail.stockUnit === 'gram' ? LOW_GRAMS : LOW_PIECES) * 4;
  return (
    <div className="dv-stats cat-stats dv-rise" style={{ ['--i' as string]: 0 }}>
      <Stat label="Price" value={amount} sub={unit} />
      {detail.isCombo ? (
        <Stat label="Stock" value="—" sub="From its items" />
      ) : (
        <Stat
          label="Available"
          value={qtyLabel(detail.available, detail.stockUnit)}
          tone={state === 'sold_out' ? 'alert' : undefined}
          sub={state === 'sold_out' ? 'Sold out' : state === 'low' ? 'Running low' : detail.onHand !== detail.available ? `${qtyLabel(detail.onHand, detail.stockUnit)} on hand` : 'In stock'}
          visual={<span className="cat-statmeter"><Meter value={detail.available / full} tone={state === 'sold_out' ? 'bad' : state === 'low' ? 'warn' : 'ok'} /></span>}
        />
      )}
      <section className="card dv-stat" aria-label="Category">
        <span className="dv-label">Category</span>
        <span className="cat-statword">{detail.categoryName ?? 'None'}</span>
        <span className="dv-sub">Sort {detail.sort ?? '—'}</span>
      </section>
    </div>
  );
}

const UNIT_HINT: Record<ProductLine, string> = { fresh: 'pc, bundle, tray of 30', grill: '1 stick, plate', mart: 'pc, pack, 1 L' };

function Basics({
  d,
  errors,
  set,
  update,
  lines,
  lineLocked,
  catOptions,
  catLoading,
}: {
  d: ProductDraft;
  errors: Errors;
  set: SetField;
  update: Update;
  lines: ProductLine[];
  lineLocked: boolean;
  catOptions: { id: string; name: string; inactive: boolean }[];
  catLoading: boolean;
}) {
  return (
    <section className="card panel" aria-labelledby="sec-basics">
      <h2 id="sec-basics">Basics</h2>
      <Field name="name" label="Name" error={errors.name}>
        <input {...invalid('name', errors.name)} className="input" maxLength={80} autoComplete="off" value={d.name} onChange={(e) => set('name', e.target.value)} />
      </Field>
      <div className="cat-grid2">
        <div className="field">
          {lineLocked ? (
            <>
              <span className="flabel">Line</span>
              <span className="row">
                <LineChip line={d.line} />
              </span>
              <span className="help">{lines.length < 2 ? 'One line shop' : 'Fixed once added'}</span>
            </>
          ) : (
            <>
              <label htmlFor={fid('line')}>Line</label>
              <Select {...invalid('line', errors.line)} className="input" value={d.line} onChange={(e) => update((x) => changeLine(x, e.target.value as ProductLine), 'line')}>
                {lines.map((l) => (
                  <option key={l} value={l}>
                    {LINE_LABEL[l]}
                  </option>
                ))}
              </Select>
            </>
          )}
          <FieldError name="line" error={errors.line} />
        </div>
        <div className="field">
          <span className="flabel">Pricing</span>
          {d.line === 'fresh' ? (
            <Segmented<'unit' | 'weight'>
              label="Pricing"
              value={d.pricing === 'weight' ? 'weight' : 'unit'}
              options={[
                { value: 'unit', label: 'By piece or pack' },
                { value: 'weight', label: 'By weight' },
              ]}
              onChange={(v) => update((x) => changeFreshPricing(x, v), 'pricing')}
            />
          ) : (
            <span className="t-label cat-static">{d.line === 'grill' ? 'Made to order' : 'Per unit'}</span>
          )}
          <FieldError name="pricing" error={errors.pricing} />
        </div>
      </div>
      {d.pricing !== 'weight' ? (
        <div className="cat-grid2">
          <Field name="price" label={d.line === 'grill' && d.combo.length ? 'Combo price' : d.line === 'mart' && d.pack.length ? 'Pack price' : 'Price'} error={errors.price} help="Past orders keep their price">
            <PesoInput name="price" value={d.price} error={errors.price} onChange={(v) => set('price', v)} />
          </Field>
          <Field name="unitLabel" label="Unit (optional)" error={errors.unitLabel} help={`Like ${UNIT_HINT[d.line]}`}>
            <input {...invalid('unitLabel', errors.unitLabel)} className="input" maxLength={24} value={d.unitLabel} onChange={(e) => set('unitLabel', e.target.value)} />
          </Field>
        </div>
      ) : null}
      <div className="cat-grid2">
        <Field name="categoryId" label="Category" error={errors.categoryId} help={catLoading ? 'Loading categories…' : catOptions.length ? undefined : 'No categories for this line yet. PopCart Ops adds them.'}>
          <Select {...invalid('categoryId', errors.categoryId)} className="input" value={d.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
            <option value="">No category</option>
            {catOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.inactive ? `${c.name} (no longer offered)` : c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field name="sort" label="Sort order" error={errors.sort} help="Lower shows first">
          <input {...invalid('sort', errors.sort)} className="input" inputMode="numeric" value={d.sort} onChange={(e) => set('sort', e.target.value)} />
        </Field>
      </div>
      <Field name="description" label="Description (optional)" error={errors.description} help={`${d.description.length}/500`}>
        <textarea {...invalid('description', errors.description)} className="input" maxLength={500} value={d.description} onChange={(e) => set('description', e.target.value)} />
      </Field>
      <Field name="source" label="Source (optional)" error={errors.source} help={d.line === 'fresh' ? 'Like Navotas fish port' : 'Supplier or origin'}>
        <input {...invalid('source', errors.source)} className="input" maxLength={80} value={d.source} onChange={(e) => set('source', e.target.value)} />
      </Field>
    </section>
  );
}

function OpeningStock({ d, errors, set, unit }: { d: ProductDraft; errors: Errors; set: SetField; unit: 'piece' | 'gram' }) {
  if ((d.line === 'grill' && d.combo.length) || (d.line === 'mart' && d.pack.length))
    return (
      <section className="card panel" aria-labelledby="sec-open">
        <h2 id="sec-open">Stock</h2>
        <p className="t-caption muted">{d.line === 'mart' ? 'A pack’s' : 'A combo’s'} stock comes from its items.</p>
      </section>
    );
  return (
    <section className="card panel" aria-labelledby="sec-open">
      <h2 id="sec-open">Opening stock</h2>
      <Field name="initialStock" label={unit === 'gram' ? 'How many kilograms you have now' : 'How many you have now'} error={errors.initialStock} help="Recorded as a restock. Leave empty for none yet.">
        <div className="input-affix cat-stockin" data-invalid={errors.initialStock ? 'true' : undefined}>
          <input {...invalid('initialStock', errors.initialStock)} inputMode={unit === 'gram' ? 'decimal' : 'numeric'} placeholder="0" value={d.initialStock} onChange={(e) => set('initialStock', e.target.value)} />
          <span className="muted">{unit === 'gram' ? 'kg' : 'pcs'}</span>
        </div>
      </Field>
    </section>
  );
}
